import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAuth } from './AuthContexte';

const NO_ACCESS = { role: 'member', allServers: false, servers: {} };

const AccessContext = createContext({ ...NO_ACCESS, loading: true, can: () => false, refresh: () => {} });

/**
 * Rôle et droits d'administration de l'utilisateur connecté (lus sur le serveur à chaque connexion,
 * donc à jour sans reconnexion quand un administrateur modifie ses droits).
 */
export const AccessProvider = ({ children }) => {
    const { token } = useAuth();
    const [access, setAccess] = useState(NO_ACCESS);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(() => {
        if (!token) {
            setAccess(NO_ACCESS);
            setLoading(false);
            return;
        }
        setLoading(true);
        api.get('/me/access')
            .then(res => setAccess({ ...NO_ACCESS, ...res.data }))
            .catch(() => setAccess(NO_ACCESS))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => { refresh(); }, [refresh]);

    const value = useMemo(() => {
        // permission : clé, tableau (l'une suffit) ou absent (n'importe quel droit sur le serveur)
        const can = (serverId, permission) => {
            if (access.allServers) return true;
            const granted = access.servers[serverId] || [];
            if (!permission) return granted.length > 0;
            return (Array.isArray(permission) ? permission : [permission]).some(p => granted.includes(p));
        };
        return {
            ...access,
            isOwner: access.role === 'owner',
            isSiteAdmin: access.allServers,
            loading,
            can,
            refresh
        };
    }, [access, loading, refresh]);

    return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

export const useAccess = () => useContext(AccessContext);
